import {
    Download as DownloadIcon,
    Edit as EditIcon, KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowUp as ArrowUpIcon
} from '@mui/icons-material';
import {
    Button, Collapse, IconButton, Skeleton, Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel, useMediaQuery,
} from '@mui/material';
import { Children, Fragment, MouseEvent, useEffect, useState, useContext } from 'react';
import { toLocalDateTimeString } from 'util/date.utils';
import { TableExpandedRow } from './TableExpandedRow';
import { ThemeContext } from 'util/themes/themeContext';
import moment from 'moment';

type Order = 'asc' | 'desc';

export interface ITableColumn {
    id: string;
    dataType: string;
    label: string;
    hasSorting: boolean;
    valueStyleOverride: any;
    onClick: Function;
    width?: string;
}

export interface ITableComponent {
    loading: boolean;
    columns: ITableColumn[];
    data: any[];
}

const sortData = (data: any[], key: string, sortOrder: Order) => {
    data.sort((a: any, b: any) => {
        if (a[key] < b[key]) {
            return sortOrder === 'asc' ? -1 : 1;
        }
        if (a[key] > b[key]) {
            return sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
    });
};

const styles = {
    button: {
        textTransform: 'none',
        color: '#457CCC',
    },
    downloadIcon: {
        color: '#457CCC',
    },
};

export function TableComponent(props: Readonly<ITableComponent>) {
    const { columns, data, loading } = props;
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<string>('');
    const [openRow, setOpenRow] = useState<number>(-1);
    const [tableRows, setTableRows] = useState<any[]>();
    const { selectedTheme } = useContext(ThemeContext);
    const isMobile = useMediaQuery(selectedTheme.breakpoints.down('md'));
    useEffect(() => {
        if (loading) {
            setTableRows(Array(5).fill({}));
        } else {
            setTableRows(data);
        }
    }, [loading]);

    const expandRow = (rowIndex: number) => {
        if (rowIndex === openRow) {
            setOpenRow(-1);
        } else {
            setOpenRow(rowIndex);
        }
    };

    const handleSortRequest =
        (columnId: string) => (_event: MouseEvent<unknown>) => {
            const isAsc = orderBy === columnId && order === 'asc';
            const newSortOrder = isAsc ? 'desc' : 'asc';
            setOrder(newSortOrder);
            setOrderBy(columnId);
            sortData(data, columnId, newSortOrder);
        };

    if (!tableRows) {
        return <></>;
    }
    return (
        <Table>
            <TableHead sx={selectedTheme.summaryTable?.header}>
                <TableRow sx={{ width: '100%' }}>
                    {Children.toArray(
                        columns.map((x) => {
                            if (!x.hasSorting) {
                                return (
                                    <TableCell
                                        sx={{
                                            ...selectedTheme?.summaryTable?.headerCell,
                                            width: x.width ?? 'auto',
                                        }}
                                    >
                                        {x.label}
                                    </TableCell>
                                );
                            }
                            return (
                                <TableCell
                                    sortDirection={
                                        orderBy === x.id ? order : 'asc'
                                    }
                                    sx={{...selectedTheme?.summaryTable?.headerCell}}
                                >
                                    <TableSortLabel
                                        active={orderBy === x.id}
                                        direction={
                                            orderBy === x.id ? order : 'asc'
                                        }
                                        onClick={handleSortRequest(x.id)}
                                        sx={{
                                            "&.MuiTableSortLabel-root": {
                                                "&:hover": {
                                                    color: selectedTheme?.palette?.primary[10],
                                                    "& .MuiTableSortLabel-icon" :{
                                                        opacity: 1
                                                    }
                                                },
                                                "&.Mui-active": {
                                                    color: selectedTheme?.palette?.primary[10]
                                                },
                                                "& .MuiTableSortLabel-icon" : {
                                                    color: selectedTheme?.palette?.secondary[200],
                                                    "&.Mui-active": {
                                                        color: selectedTheme?.palette?.secondary[200]
                                                    }
                                                },
                                            },
                                        }}
                                    >
                                        {x.label}
                                    </TableSortLabel>
                                </TableCell>
                            );
                        })
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                {Children.toArray(
                    tableRows.map((row: any, index: number) => {
                        return (
                            <Fragment>
                                <TableRow sx={selectedTheme.summaryTable?.row}>
                                    {Children.toArray(
                                        columns.map((column) => {
                                            let value;
                                            switch (column.dataType) {
                                                case 'date': {
                                                    let date =
                                                        toLocalDateTimeString(
                                                            row[column.id]
                                                        )?.replace('am','AM')?.replace('pm','PM');
                                                    if (
                                                        date !== 'Invalid date'
                                                    ) {
                                                        value = date;
                                                        if(isMobile){
                                                        value = moment(date).format('L');
                                                        }
                                                    } else {
                                                        value = row[column.id];
                                                    }
                                                    break;
                                                }
                                                case 'downloadButton':
                                                    if (isMobile) {
                                                        value = (
                                                            <DownloadIcon
                                                                onClick={() =>
                                                                    column.onClick(
                                                                        row
                                                                    )
                                                                }
                                                                sx={
                                                                    styles.downloadIcon
                                                                }
                                                            />
                                                        );
                                                    } else {
                                                        value = (
                                                            <Button
                                                         
                                                                size={'small'}
                                                                variant={
                                                                    'outlined'
                                                                }
                                                                color="secondary"
                                                                sx={{
                                                                    textTransform:
                                                                        'none',
                                                                }}
                                                                onClick={() =>
                                                                    column.onClick(
                                                                        row
                                                                    )
                                                                }
                                                            >
                                                                Download
                                                            </Button>
                                                        );
                                                    }
                                                    break;
                                                case 'editButton':
                                                    if (isMobile) {
                                                        value = (
                                                            <EditIcon
                                                                onClick={() =>
                                                                    column.onClick(
                                                                        row
                                                                    )
                                                                }
                                                            
                                                                sx={
                                                                    styles.downloadIcon
                                                                }
                                                            />
                                                        );
                                                    } else {
                                                        value = (
                                                            <Button
                                                               
                                                                size={'small'}
                                                                variant={
                                                                    'outlined'
                                                                }
                                                                color="secondary"
                                                                sx={{
                                                                    textTransform:
                                                                        'none',
                                                                }}
                                                                onClick={() =>
                                                                    column.onClick(
                                                                        row
                                                                    )
                                                                }
                                                            >
                                                                Edit
                                                            </Button>
                                                        );
                                                    }
                                                    break;
                                                case 'expand':
                                                    value = (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                expandRow(index)
                                                            }
                                                       
                                                        >
                                                            {openRow ===
                                                            index ? (
                                                                <ArrowUpIcon
                                                                
                                                                />
                                                            ) : (
                                                                <ArrowDownIcon
                                                              
                                                                />
                                                            )}
                                                        </IconButton>
                                                    );
                                                    break;
                                                default:
                                                    value = row[column.id];
                                            }
                                            return (
                                                <TableCell
                                                    sx={{
                                                        ...selectedTheme?.summaryTable?.rowCell,
                                                        ...column.valueStyleOverride,
                                                        width:
                                                            column.width ??
                                                            'auto',
                                                        borderBottom: '1px solid #4653F6',
                                                        [selectedTheme.breakpoints.down('md')]:{
                                                            wordBreak:'break-word',
                                                            whiteSpace:'normal',
                                                        },
                                                    }}
                                                >
                                                    { loading ? <Skeleton/> : value }
                                                </TableCell>
                                            );
                                        })
                                    )}
                                </TableRow>
                                <TableRow 
                                    sx={selectedTheme.summaryTable?.rowExpanded}
                                >
                                    <TableCell
                                        style={{
                                            paddingBottom: 0,
                                            paddingTop: 0,
                                            border: 'none'
                                        }}
                                        colSpan={columns.length}
                                    >
                                        <Collapse
                                            in={openRow === index}
                                            timeout={'auto'}
                                            unmountOnExit
                                        >
                                            <TableExpandedRow
                                                rows={row.expandedRowValue}
                                                isExpanded={openRow === index}
                                                isLastRow={false}
                                            />
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        );
                    })
                )}
            </TableBody>
        </Table>
    );
}
